/**
 * ConversationRoster — who is in a conversation (conversation-members spec): every member with its kind, the lead, and
 * whether it is working on a message right now. The owner (or a person who manages agents) chooses the lead; a person
 * who manages agents adds and removes members. An AI member that has answered works on a thread of its own under the
 * conversation, and its row opens that thread in the same view (owner 2026-10-31). Demo page: /chats (the context
 * column's people list), as `ItemList` rows with a ⋯ `ActionMenu` each.
 */
import {
  type ConversationMemberRef,
  isAiMemberType,
  memberKindLabel,
} from '@confluent/shared/lib/conversation-addressing';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Crown, UserMinus, UserPlus } from 'lucide-react';
import { useState } from 'react';
import {
  listConversationMembersQueryKey,
  removeConversationMemberMutation,
  setConversationLeadMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { Can } from '../auth/Can.js';
import { ActionMenu, type ActionMenuItem } from '../ui/ActionMenu.js';
import { Avatar } from '../ui/Avatar.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { ItemList } from '../ui/ItemList.js';
import { useToast } from '../ui/Toast.js';
import { AddMemberDialog } from './AddMemberDialog.js';

interface ConversationRosterProps {
  conversationId: string;
  members: readonly ConversationMemberRef[];
  me: ConversationMemberRef | null;
  /** Members working on a message right now, by address. */
  working: ReadonlySet<number>;
  /** Each AI member's own thread, by address (`useConversationMembers().threads`). */
  threads?: ReadonlyMap<number, number>;
  /** Opens a conversation in place of this one; without it the rows only show who is here. */
  onOpenConversation?: (conversationId: string) => void;
}

export function ConversationRoster({
  conversationId,
  members,
  me,
  working,
  threads,
  onOpenConversation,
}: ConversationRosterProps) {
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePolicy('CanManageAgents');
  const canChooseLead = canManage || me?.role === 'owner';
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ConversationMemberRef | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: listConversationMembersQueryKey({ path: { id: conversationId } }) });
  const setLead = useMutation({
    ...setConversationLeadMutation(),
    onSuccess: () => void refresh(),
    onError: (error) => notify(error.message || 'Could not change the lead.', 'danger'),
  });
  const remove = useMutation({
    ...removeConversationMemberMutation(),
    onSuccess: () => {
      void refresh();
      setRemoving(null);
    },
    onError: (error) => notify(error.message || 'Could not remove the member.', 'danger'),
  });

  const actionsFor = (member: ConversationMemberRef): ActionMenuItem[] => [
    ...(canChooseLead && isAiMemberType(member.memberType) && member.role !== 'lead'
      ? [
          {
            id: 'lead',
            label: 'Make lead',
            icon: <Crown size={14} aria-hidden />,
            onAction: () => setLead.mutate({ path: { id: conversationId, memberId: String(member.id) } }),
          },
        ]
      : []),
    ...(canManage && member.id !== me?.id && member.role !== 'owner'
      ? [
          {
            id: 'remove',
            label: 'Remove',
            icon: <UserMinus size={14} aria-hidden />,
            tone: 'danger' as const,
            onAction: () => setRemoving(member),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-2">
      <ItemList
        aria-label="Members of this conversation"
        items={members.map((member) => {
          const busy = working.has(member.id);
          const actions = actionsFor(member);
          const thread = threads?.get(member.id);
          const ai = isAiMemberType(member.memberType) && member.role !== 'lead';
          return {
            id: String(member.id),
            leading: (
              <Avatar
                kind={member.memberType === 'user' ? 'human' : 'ai'}
                label={member.name}
                size="sm"
                {...(busy ? { status: 'working' as const } : {})}
              />
            ),
            title: (
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="truncate">{member.id === me?.id ? `${member.name} (you)` : member.name}</span>
                {member.role === 'lead' && <Badge variant="info">Lead</Badge>}
              </span>
            ),
            subtitle: busy
              ? 'Working…'
              : ai && onOpenConversation && thread === undefined
                ? `${memberKindLabel(member.memberType)} · no thread yet`
                : memberKindLabel(member.memberType),
            ...(onOpenConversation && thread !== undefined
              ? { onPress: () => onOpenConversation(String(thread)), pressLabel: `Open ${member.name}'s thread` }
              : {}),
            ...(actions.length > 0
              ? { value: <ActionMenu items={actions} aria-label={`Actions for ${member.name}`} /> }
              : {}),
          };
        })}
      />
      <Can policy="CanManageAgents">
        <Button variant="ghost" size="sm" className="self-start" onClick={() => setAdding(true)}>
          <UserPlus size={14} aria-hidden />
          Add a member
        </Button>
      </Can>
      {adding && (
        <AddMemberDialog
          conversationId={conversationId}
          open={adding}
          onClose={() => setAdding(false)}
          members={members}
        />
      )}
      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) remove.mutate({ path: { id: conversationId, memberId: String(removing.id) } });
        }}
        title="Remove member"
        message={`Remove ${removing?.name ?? 'this member'} from the conversation? It stops receiving messages; what it wrote stays.`}
        confirmLabel="Remove"
        tone="danger"
        loading={remove.isPending}
      />
    </div>
  );
}
